import React, { useContext, useEffect, useRef, useState } from 'react'

import { WithOptimizelyProps, withOptimizely } from '@optimizely/react-sdk'
import { UserInfo } from '@optimizely/react-sdk/dist/utils'
import classNames from 'classnames'
import get from 'lodash/get'
import type { GetServerSideProps, NextPage } from 'next'
import { useCookies } from 'react-cookie'

import MainLayout from '@/components/layouts/main-layout'
import type { ProductionPageSsrRequestsType } from '@/components/pages/production/types'
import { ACCESSIBILITY_IDS, CATEGORY_IDS, PRICE_GROUP_COOKIES, STYLES } from '@/constants'
import { BraintreeClientTokenProvider } from '@/context/braintree'
import InternationalizationContext from '@/context/internationalization/context'
import { LayoutProvider } from '@/context/layout'
import { ListingsProvider } from '@/context/listings'
import type { ListingsParamsType } from '@/context/listings/types'
import { MapProvider } from '@/context/map'
import {
    ProductionDetailsProvider,
    serverSideRead as serverSideReadProductionDetails,
} from '@/context/production-details'
import { PromoProvider, serverSideRead as serverSideReadPromo } from '@/context/promo'
import type { PromoResponse } from '@/context/promo/types'
import { RegionProvider } from '@/context/region'
import { UrgencyVelocityProvider, serverSideRead as serverSideReadUrgencyVelocity } from '@/context/urgency-velocity'
import type { UrgencyVelocityResponse } from '@/context/urgency-velocity/types'
import userDataContext from '@/context/user-data/context'
import useExitIntentTracking from '@/hooks/use-exit-intent'
import { useRecentlyViewed } from '@/hooks/use-recently-viewed'
import useWindowSize from '@/hooks/use-window-size'
import { OPTIMIZELY_EVENT } from '@/optimizely/constants'
import { useLocalizedCurrencyFeature } from '@/optimizely/features/internationalization/hooks/localized-currency'
import type { Metadata, ProductionDetail } from '@/types/app-types'
import { trackProductionPageView } from '@/utils/analytics'
import { ProductionPageData, initGTM, trackEvent } from '@/utils/analytics/ga360'
import { getMainPerformer } from '@/utils/production'
import { getSsrSearchParams } from '@/utils/query-strings'

import { FilterProvider } from './components/context'
import DateSwitcherSheet from './components/date-switcher/components/date-switcher-sheet'
import { DateSwitcherProvider } from './components/date-switcher/context'
import FiltersDesktop from './components/filters/desktop/filters-desktop'
import FiltersMobile from './components/filters/mobile/filters-mobile'
import LdJsonProduction from './components/ld-json-production'
import ListingHeader from './components/listing-header'
import ListingsList from './components/listings-list'
import MapWrapper from './components/map-wrapper'
import MoreInfoModal from './components/more-info-modal'
import { MoreInfoProvider } from './components/more-info-modal/context'
import ProductionDetailBar from './components/production-detail-bar'
import SeeSimilarSnackbar from './components/see-similar-snackbar'
import TicketDetails from './components/ticket-details'
import { TicketDetailsProvider } from './components/ticket-details/context'
import TicketsNoLongerAvailable from './components/tickets-no-longer-available'
import useOptimizelyUser from './hooks/use-optimizely-user'
import usePricingFeatureSlots from './hooks/use-pricing-feature-slots'
import useTicketsNoLongerAvailable from './hooks/use-tickets-no-longer-available'
import styles from './styles.module.scss'
import { getMetadata, getPromoCode } from './utils'

export interface ProductionPageProps extends WithOptimizelyProps {
    id: number
    initialProductionDetailsData: { data: ProductionDetail | undefined }
    initialUrgencyVelocityData: { data: UrgencyVelocityResponse | undefined }
    initialPromoData: { data: PromoResponse | undefined }
    metadata: Metadata
    promoTriggerAttr: string
    referer?: string
}

const ProductionPage: NextPage<ProductionPageProps> = ({
    id,
    initialProductionDetailsData,
    initialUrgencyVelocityData,
    initialPromoData,
    metadata,
    optimizely,
    promoTriggerAttr,
    referer,
}) => {
    const showUsd = initialProductionDetailsData.data?.venue.countryCode !== 'US'
    const [showMobileFilters, setShowMobileFilters] = useState(false)
    const mapContainerRef = useRef<HTMLDivElement>(null)
    const scrollRef = useRef<HTMLDivElement>(null)
    const production = initialProductionDetailsData?.data
    const { width: windowWidth } = useWindowSize()
    // using isTypeDesktop or useDesktopMediaQuery does not work for this page when used in useState
    const isDesktop = !!windowWidth && windowWidth > STYLES.breakpoint.desktop

    const [, setCookie, removeCookie] = useCookies(PRICE_GROUP_COOKIES)
    const { addRecentlyViewed } = useRecentlyViewed()
    const {
        dispatch: { updatePriceGroupId },
    } = useContext(userDataContext)
    const {
        selectors: { selectedCurrency },
    } = useContext(InternationalizationContext)

    const mainPerformer = production ? getMainPerformer(production) : undefined
    const categoryId = mainPerformer?.category.id || -1
    const subCategoryId = mainPerformer?.category.subCategories?.[0].id || -1
    const performerId = mainPerformer?.id

    const regionId = production?.venue?.regionId || -1

    // Custom hook to set Optimizely user attributes
    useOptimizelyUser(optimizely, production?.venue.state, performerId, categoryId, subCategoryId)
    const [isLocalizedCurrencyFeatureEnabled] = useLocalizedCurrencyFeature()

    const [isMapOpen, setIsMapOpen] = useState(!isDesktop)

    /* istanbul ignore next */
    const pricingModel = usePricingFeatureSlots(categoryId, subCategoryId)

    const priceGroupId: string | undefined = get(pricingModel, 'variables.priceGroupId')

    useExitIntentTracking(production?.id, mainPerformer?.id)

    useEffect(() => {
        if (production) {
            addRecentlyViewed(production)
        }
    }, [production, addRecentlyViewed])

    useEffect(() => {
        if (!!priceGroupId) {
            setCookie(PRICE_GROUP_COOKIES[4], priceGroupId, { path: '/' })
            updatePriceGroupId(priceGroupId)
        } else {
            removeCookie(PRICE_GROUP_COOKIES[4], { path: '/' })
            updatePriceGroupId(undefined)
        }
    }, [priceGroupId, removeCookie, setCookie, updatePriceGroupId])

    const listingParams: ListingsParamsType = {
        productionId: id,
        includeIpAddress: true,
        currency: isLocalizedCurrencyFeatureEnabled ? selectedCurrency : '',
        ...(pricingModel.enabled && !!priceGroupId && { priceGroupId: parseInt(priceGroupId) }),
    }

    const layoutParams = { productionId: id }

    useEffect(() => {
        if (typeof window !== 'undefined' && initialProductionDetailsData.data)
            trackProductionPageView(initialProductionDetailsData.data)
    }, [])

    const mergedListingsContainerClassName = classNames(styles.listingsContainer, {
        [styles.listingsContainerMapOpen]: isMapOpen,
        [styles.variantListingsContainer]: !isDesktop,
    })

    const mergedListingsListClassName = classNames(styles.listingsList, {
        [styles.offsetOpenMapContainerHeight]: isMapOpen,
        [styles.miniMapInHero]: !isDesktop,
    })

    const { areTicketsNoLongerAvailable } = useTicketsNoLongerAvailable()

    useEffect(() => {
        if (window) {
            const masterPerformer = production?.performers?.find(({ master }) => master)

            // __APP_DATA__ is used by Google Tag Manager to report analytics events to Braze and other 3rd parties
            window.__APP_DATA__ = {
                performerId: masterPerformer?.id,
                performerName: masterPerformer?.name,
                categoryName: masterPerformer?.category.name,
                subCategoryName: masterPerformer?.category.subCategories?.[0]?.name,
            }

            initGTM({
                eventId: masterPerformer?.id,
                productionId: id,
                eventName: masterPerformer?.name,
                productionName: production?.name,
                purchaseCategory: production?.performers?.[0]?.category.name,
                purchaseSubCategory: production?.performers?.[0]?.category.subCategories?.[0]?.name,
                noTixError: areTicketsNoLongerAvailable,
            } as ProductionPageData)
        }

        return () => {
            if (window) {
                // Cleanup
                window.__APP_DATA__ = undefined
            }
        }
    }, [id, production, areTicketsNoLongerAvailable])

    useEffect(() => {
        optimizely?.track(OPTIMIZELY_EVENT.productionPageView)
        if (production?.performers[0].category.id === CATEGORY_IDS.THEATER) {
            optimizely?.track(OPTIMIZELY_EVENT.theaterProductionPageView)
        }
        if (production?.performers[0].category.id === CATEGORY_IDS.SPORTS) {
            optimizely?.track(OPTIMIZELY_EVENT.sportsProductionPageView)
        }
        if (production?.performers[0].category.id === CATEGORY_IDS.CONCERTS) {
            optimizely?.track(OPTIMIZELY_EVENT.concertProductionPageView)
        }
    }, [id])

    const setUser = (user: UserInfo | undefined) => {
        if (user) optimizely?.setUser(user)
    }

    return (
        <div className={styles.pageContainer} id={ACCESSIBILITY_IDS.CONTENT}>
            <ProductionDetailsProvider initialParams={{ productionId: id }} initialProps={initialProductionDetailsData}>
                <TicketsNoLongerAvailable />
                <MainLayout
                    shouldRenderPageWrapper={false}
                    shouldRenderFooter={false}
                    metadata={metadata}
                    mobileOnlyPaidSearchDisclaimer={!isDesktop}
                >
                    <LayoutProvider initialParams={layoutParams}>
                        <ListingsProvider initialParams={listingParams}>
                            <LdJsonProduction />
                            <FilterProvider>
                                <TicketDetailsProvider>
                                    <MapProvider>
                                        <RegionProvider initialParams={{ regionId }}>
                                            <MoreInfoProvider>
                                                <PromoProvider
                                                    initialParams={{ promoTriggerAttr }}
                                                    initialProps={initialPromoData}
                                                >
                                                    <BraintreeClientTokenProvider>
                                                        <TicketDetails
                                                            setUser={(user) => setUser(user)}
                                                            referer={referer}
                                                        />
                                                    </BraintreeClientTokenProvider>
                                                    <MoreInfoModal />
                                                    <DateSwitcherProvider>
                                                        <UrgencyVelocityProvider
                                                            initialParams={{ resource: 'PRODUCTION', resourceId: id }}
                                                            initialProps={initialUrgencyVelocityData}
                                                        >
                                                            {!windowWidth ? (
                                                                <ProductionDetailBar isPlainPageSource />
                                                            ) : (
                                                                <>
                                                                    {!isDesktop ? (
                                                                        <>
                                                                            <ListingHeader
                                                                                clickFilters={() => {
                                                                                    setShowMobileFilters(
                                                                                        !showMobileFilters,
                                                                                    )
                                                                                }}
                                                                                isMapOpen={isMapOpen}
                                                                                ref={mapContainerRef}
                                                                                referer={referer}
                                                                                toggleMap={() => {
                                                                                    setIsMapOpen(!isMapOpen)
                                                                                }}
                                                                            />

                                                                            <div className={styles.contentContainer}>
                                                                                <div
                                                                                    className={styles.sidebar}
                                                                                    ref={scrollRef}
                                                                                >
                                                                                    <div
                                                                                        className={
                                                                                            mergedListingsContainerClassName
                                                                                        }
                                                                                        data-testid="listings-container"
                                                                                    >
                                                                                        <ListingsList
                                                                                            exposedFilter
                                                                                            isMapOpen={isMapOpen}
                                                                                            mapContainerRef={
                                                                                                mapContainerRef
                                                                                            }
                                                                                            scrollRef={scrollRef}
                                                                                            className={
                                                                                                mergedListingsListClassName
                                                                                            }
                                                                                            showUsd={showUsd}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <div className={styles.contentContainer}>
                                                                            <div className={styles.sidebar}>
                                                                                <ListingHeader
                                                                                    clickFilters={() => {}}
                                                                                    isMapOpen={false}
                                                                                />

                                                                                <div
                                                                                    className={styles.listingsContainer}
                                                                                    data-testid="listings-container"
                                                                                    ref={scrollRef}
                                                                                >
                                                                                    <ListingsList
                                                                                        isMapOpen={isMapOpen}
                                                                                        mapContainerRef={
                                                                                            mapContainerRef
                                                                                        }
                                                                                        scrollRef={scrollRef}
                                                                                        className={
                                                                                            mergedListingsListClassName
                                                                                        }
                                                                                        showUsd={showUsd}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            <div
                                                                                className={styles.mapAndFilterContainer}
                                                                            >
                                                                                <FiltersDesktop />
                                                                                <div
                                                                                    className={styles.mapFrame}
                                                                                    ref={mapContainerRef}
                                                                                    data-testid="map-frame"
                                                                                >
                                                                                    <MapWrapper
                                                                                        isDesktop={isDesktop}
                                                                                        scrollRef={scrollRef}
                                                                                        displayFooter
                                                                                        withPanAndZoom
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </UrgencyVelocityProvider>
                                                        <DateSwitcherSheet />
                                                    </DateSwitcherProvider>
                                                </PromoProvider>
                                            </MoreInfoProvider>
                                        </RegionProvider>
                                        <FiltersMobile
                                            showMobileFilters={showMobileFilters}
                                            toggleShowMobileFilters={() => {
                                                setShowMobileFilters(!showMobileFilters)
                                                if (showMobileFilters)
                                                    trackEvent(
                                                        'ProductionPageFilters',
                                                        'OpenedFilterModal',
                                                        undefined,
                                                        undefined,
                                                        false,
                                                    )
                                            }}
                                        />
                                        <SeeSimilarSnackbar />
                                    </MapProvider>
                                </TicketDetailsProvider>
                            </FilterProvider>
                        </ListingsProvider>
                    </LayoutProvider>
                </MainLayout>
            </ProductionDetailsProvider>
        </div>
    )
}

ProductionPage.displayName = 'ProductionPage'

export const getServerSideProps: GetServerSideProps<ProductionPageProps> = async (context) => {
    const { query, res, req, params } = context
    const { referer } = req.headers
    const { id, slug } = query
    const productionId = parseInt(id as string, 10)
    let productionDetailsData: ProductionDetail | undefined
    let urgencyVelocityData: UrgencyVelocityResponse | undefined
    let promoData: PromoResponse | undefined
    const promoTriggerAttr = getPromoCode(req.cookies, query)
    const searchParamsString = getSsrSearchParams({ query, params })
    const isServerSide = typeof window === 'undefined'

    try {
        const [settledDetailsData, settledUrgencyVelocityData, settledPromoData] =
            await Promise.allSettled<ProductionPageSsrRequestsType>([
                serverSideReadProductionDetails({ productionId }),
                serverSideReadUrgencyVelocity({ resource: 'PRODUCTION', resourceId: Number(id) }),
                serverSideReadPromo({ promoTriggerAttr }),
            ])

        // required fetch. should jump to `catch` block when error but not the others
        if (settledDetailsData.status === 'fulfilled') {
            productionDetailsData = settledDetailsData.value
        } else throw settledDetailsData.reason
        // supplemental/optional fetch (where error is gracefully handled)
        promoData = settledPromoData.status === 'fulfilled' ? settledPromoData.value : undefined
        urgencyVelocityData =
            settledUrgencyVelocityData.status === 'fulfilled' ? settledUrgencyVelocityData.value : undefined

        if (!!productionDetailsData && !!productionDetailsData.hidden) {
            // Hermes sorts the main performer to the start of the performers array
            const { webPath, organicUrl } = productionDetailsData.performers[0]
            const performerUrl = webPath || organicUrl

            res.writeHead(302, 'Production hidden', { Location: `${performerUrl}${searchParamsString}` })
            res.end()

            const props = {
                id: productionId,
                initialProductionDetailsData: { data: productionDetailsData },
                initialUrgencyVelocityData: { data: urgencyVelocityData },
                initialPromoData: { data: promoData },
                isServerSide,
                metadata: {},
                optimizely: null,
                optimizelyReadyTimeout: undefined,
                promoTriggerAttr: '',
                referer,
            }

            return { props: JSON.parse(JSON.stringify(props)) }
        }

        if (slug && `/${slug}/production/${id}` !== productionDetailsData?.webPath) {
            res.writeHead(301, { Location: `${productionDetailsData?.webPath}${searchParamsString}` })
            res.end()

            const props = {
                id: productionId,
                initialProductionDetailsData: { data: productionDetailsData },
                initialUrgencyVelocityData: { data: urgencyVelocityData },
                initialPromoData: { data: promoData },
                isServerSide,
                metadata: {},
                optimizely: null,
                optimizelyReadyTimeout: undefined,
                promoTriggerAttr: '',
                referer,
            }

            return { props: JSON.parse(JSON.stringify(props)) }
        }
    } catch (e) {
        console.error((e as Error).message)

        res.writeHead(302, 'Not found', { Location: `/404?productionId=${productionId}` })
        res.end()

        const props = {
            id: productionId,
            initialProductionDetailsData: { data: productionDetailsData },
            initialUrgencyVelocityData: { data: urgencyVelocityData },
            initialPromoData: { data: promoData },
            isServerSide,
            metadata: {},
            optimizely: null,
            optimizelyReadyTimeout: undefined,
            promoTriggerAttr: '',
            referer,
        }

        return { props: JSON.parse(JSON.stringify(props)) }
    }

    const props = {
        id: productionId,
        initialProductionDetailsData: { data: productionDetailsData },
        initialUrgencyVelocityData: { data: urgencyVelocityData },
        initialPromoData: { data: promoData },
        isServerSide,
        metadata: getMetadata(productionDetailsData),
        optimizely: null,
        optimizelyReadyTimeout: undefined,
        promoTriggerAttr,
        referer,
    }

    // NextJs doesn't like undefined so this is one way to remediate it , see also https://github.com/vercel/next.js/discussions/11209
    return { props: JSON.parse(JSON.stringify(props)) }
}

export default withOptimizely(ProductionPage)
